import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useDebounce } from 'uikit';

import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { uerIsUniqueContractObjectCodeGql } from '../../workOrderQueries';

interface IUerIsUniqueContractObjectCode {
    code: string | undefined;
    contractObjectId: number | undefined;
    contractId: number | undefined;
    isDirtyObjectCode?: boolean;
}

export const useUerIsUniqueContractObjectCode = ({
    code,
    contractObjectId,
    contractId,
    isDirtyObjectCode,
}: IUerIsUniqueContractObjectCode) => {
    const dispatch = useDispatch();
    const trimmedCode = code?.trim();
    // Keep responses for earlier input values out of the current validation state.
    const requestId = `uerIsUniqueContractCodeName_${contractId}_${contractObjectId}_${trimmedCode}`;

    const [data, status, error, makeRequest] = useRequest({
        requestId,
        query: uerIsUniqueContractObjectCodeGql,
        withEffect: false,
    });

    const debounceRequest = useDebounce(() => {
        if (contractId && trimmedCode && isDirtyObjectCode) {
            dispatch(makeRequest({ contractId, contractObjectId, code: trimmedCode }));
        }
    }, 300);

    const [reset] = useResetStoreData({ requestId });

    useEffect(() => {
        if (trimmedCode && isDirtyObjectCode) {
            debounceRequest();
        }
    }, [trimmedCode, isDirtyObjectCode, debounceRequest]);

    useEffect(
        () => () => {
            reset(undefined);
        },
        [reset],
    );

    const isNotUnique = status === RequestStatus.SUCCESS && data?.uerIsUniqueContractObjectCode?.result === false;
    const isCheckError = isDirtyObjectCode && !!trimmedCode && status === RequestStatus.ERROR;

    return {
        result:
            !isDirtyObjectCode ||
            !trimmedCode ||
            (status === RequestStatus.SUCCESS && data?.uerIsUniqueContractObjectCode?.result === true),
        isNotUnique,
        isCheckError,
        uerIsUniqueContractObjectCodeStatus: status,
        uerIsUniqueContractObjectCodeError: error,
    };
};
